import { InputProps, TextField, ThemeProvider } from "@material-ui/core";
import { AutocompleteRenderInputParams } from "@material-ui/lab";
import { useEffect, useRef, useState } from "react";
import { newLyfeTheme } from "../theme/newLyfeTheme";
import Typography from "../Typography/Typography";

interface NewCompleteInputProps {
	params: AutocompleteRenderInputParams;
	label: string;
	required?: boolean;
	disabled?: boolean;
	error?: string;
	name?: string;
	noChips?: true;
}

export default function NewCompleteInput(props: NewCompleteInputProps) {
	const { params, label, required, name, noChips } = props;
	const startAdornments = params.InputProps.startAdornment as Array<any> | undefined;
	const [showStartAdornment, setShowStartAdornment] = useState(true);
	const [input, setInput] = useState("");
	const prevStartAdornmentsLength = useRef(startAdornments?.length);

	useEffect(() => {
		if (!noChips || !prevStartAdornmentsLength) return;
		if (startAdornments?.length === 0) setShowStartAdornment(false);
		if (prevStartAdornmentsLength.current !== startAdornments?.length) {
			setShowStartAdornment(true);
			prevStartAdornmentsLength.current = startAdornments?.length;
			setInput("");
		}
	}, [startAdornments?.length]);

	const renderCaretColor = (): "inherit" | "transparent" => {
		if (!noChips) return "inherit";
		if (!startAdornments || startAdornments?.length === 0) return "inherit";
		if (!showStartAdornment || input.length > 0) return "inherit";
		return "transparent";
	};

	const startAdornment = (
		<StartAdornment
			{...props}
			visible={showStartAdornment}
			startAdornments={startAdornments}
			setShowStartAdornment={setShowStartAdornment}
			input={input}
		/>
	);

	return (
		<ThemeProvider theme={newLyfeTheme}>
			<TextField
				name={name}
				error={!!props.error}
				disabled={props.disabled}
				helperText={props.error}
				value={input ? input : undefined}
				{...{
					params,
					InputProps: {
						...params.InputProps,
						startAdornment: noChips ? startAdornment : params.InputProps.startAdornment,
					},
				}}
				label={label}
				variant="outlined"
				fullWidth
				onBlurCapture={() => {
					setShowStartAdornment(true);
				}}
				onFocusCapture={() => setShowStartAdornment(false)}
				onChange={(e) => setInput(e.target.value)}
				required={required}
				inputProps={{
					...params.inputProps,
					style: {
						...(params.inputProps as InputProps["inputProps"])?.style,
						caretColor: renderCaretColor(),
					},
				}}
			/>
		</ThemeProvider>
	);
}

interface StartAdornmentProps {
	visible: boolean;
	setShowStartAdornment: React.Dispatch<React.SetStateAction<boolean>>;
	input: string;
	startAdornments: any[] | undefined;
}

const StartAdornment: React.FC<StartAdornmentProps & NewCompleteInputProps> = ({
	visible,
	noChips,
	params,
	setShowStartAdornment,
	input,
	startAdornments,
}) => {
	if (!noChips) return <>{params.InputProps.startAdornment}</>;
	if (!visible || input.length > 0) return null;
	return (
		<Typography
			onClick={() => setShowStartAdornment(false)}
			noWrap
			style={{
				marginTop: startAdornments && startAdornments?.length > 1 ? "5px" : "0px",
			}}
		>
			{startAdornments?.map((a, i) => (i < startAdornments?.length - 1 ? `${a.props.label}, ` : `${a.props.label}`))}
		</Typography>
	);
};
